import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [location, setLocation] = useState(null);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Obtener datos del usuario
        const userData = await apiService.getUser(id);
        setUser(userData);
        
        // Obtener ubicación
        if (userData.location_ubigeo) {
          const locationData = await apiService.getLocationUbigeo(userData.location_ubigeo);
          setLocation(locationData);
        }
        
        // Usar los roles que ya vienen en la respuesta del usuario
        if (userData.roles) {
          setRoles(userData.roles.map(role => ({
            id: role.id,
            role: { id: role.id, role_name: role.name },
            created_at: new Date().toISOString() // Fecha actual como fallback
          })));
        }
        
        // Usar las sucursales que ya vienen en la respuesta del usuario
        if (userData.branches) {
          setBranches(userData.branches.map(branch => ({
            id: branch.id,
            branch: { id: branch.id, branch_name: branch.name },
            created_at: new Date().toISOString() // Fecha actual como fallback
          })));
        }
        
      } catch (err) {
        console.error('Error al cargar datos del usuario:', err);
        setError('Error al cargar los datos del usuario. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  const handleBack = () => {
    navigate('/users');
  };

  const handleEdit = () => {
    navigate(`/users/edit/${id}`);
  };

  const getDocumentTypeLabel = (type) => {
    switch (type) {
      case 'DNI':
        return 'DNI';
      case 'CE':
        return 'Carnet de Extranjería';
      case 'PAS':
        return 'Pasaporte';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error || 'Usuario no encontrado'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Typography variant="h4">
            Detalles del Usuario
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Editar
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', mr: 2 }}>
                <PersonIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h5">
                  {user.first_name} {user.father_last_name} {user.mother_last_name}
                </Typography>
                <Chip
                  label={user.is_enabled ? 'Activo' : 'Inactivo'}
                  color={user.is_enabled ? 'success' : 'error'}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Documento
                </Typography>
                <Typography variant="body1">
                  {getDocumentTypeLabel(user.document_type)}: {user.document_number}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Box display="flex" alignItems="center">
                  <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">{user.email}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Teléfono
                </Typography>
                <Box display="flex" alignItems="center">
                  <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">{user.phone_prefix} {user.phone_number}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Ubicación
                </Typography>
                <Box display="flex" alignItems="center">
                  <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body1">
                    {location ? `${location.district_name}, ${location.province_name}, ${location.department_name}` : 'No disponible'}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Dirección
                </Typography>
                <Typography variant="body1">{user.address}</Typography>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Fecha de Creación
                </Typography>
                <Typography variant="body1">
                  {new Date(user.created_at).toLocaleString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Última Actualización
                </Typography>
                <Typography variant="body1">
                  {new Date(user.updated_at).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title="Roles Asignados" 
              avatar={<AssignmentIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              {roles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tiene roles asignados
                </Typography>
              ) : (
                <List>
                  {roles.map((userRole) => (
                    <ListItem key={userRole.id}>
                      <ListItemIcon>
                        <AssignmentIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={userRole.role.role_name} 
                        secondary={`Asignado: ${new Date(userRole.created_at).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader 
              title="Sucursales Asignadas" 
              avatar={<BusinessIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              {branches.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tiene sucursales asignadas
                </Typography>
              ) : (
                <List>
                  {branches.map((userBranch) => (
                    <ListItem key={userBranch.id}>
                      <ListItemIcon>
                        <BusinessIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary={userBranch.branch.branch_name} 
                        secondary={`Asignado: ${new Date(userBranch.created_at).toLocaleDateString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetail; 